import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Play, Pause, ChevronLeft, ChevronRight, RotateCcw, X, List, Volume2, VolumeX, Link as LinkIcon, Music, Music2, ArrowRight, Layers, Gauge, Users, Sparkles, Repeat, Subtitles, Mail, Star, QrCode, Check } from "lucide-react";
import musOrchestra from "@/assets/demo/audio/music_orchestra.mp3";
import musPiano from "@/assets/demo/audio/music_piano.mp3";
import musPop from "@/assets/demo/audio/music_pop.mp3";

const MUSIC_TRACKS = [
  { key: "orchestra", label: "Orchestra", src: musOrchestra },
  { key: "piano", label: "Soft piano", src: musPiano },
  { key: "pop", label: "Upbeat pop", src: musPop },
];

import imgOnboarding from "@/assets/demo/onboarding.jpg";
import imgAiPlan from "@/assets/demo/aiplan.jpg";
import imgWbs from "@/assets/demo/wbs.jpg";
import imgVelocity from "@/assets/demo/velocity.jpg";
import imgTrueup from "@/assets/demo/trueup.jpg";
import imgAutopilot from "@/assets/demo/autopilot.jpg";
import imgVisualMap from "@/assets/demo/visualmap.jpg";
import imgDashboard from "@/assets/demo/dashboard.jpg";
import imgRaid from "@/assets/demo/raid.jpg";
import imgStatus from "@/assets/demo/statusreport.jpg";
import imgAgenda from "@/assets/demo/agenda.jpg";
import imgNotebook from "@/assets/demo/notebook.jpg";
import imgMaestro from "@/assets/demo/maestro.jpg";
import imgLibrary from "@/assets/demo/library.jpg";
import imgMaestroDoc from "@/assets/demo/maestrodoc.jpg";
import imgAudit from "@/assets/demo/audit.jpg";
import imgCards from "@/assets/demo/cards.jpg";
import imgDone from "@/assets/demo/done.jpg";
import imgArchived from "@/assets/demo/archived.jpg";
import imgLessons from "@/assets/demo/lessons.jpg";
import imgPmo from "@/assets/demo/pmo.jpg";
import imgWizard from "@/assets/demo/wizard.jpg";
import imgGantt from "@/assets/demo/gantt.jpg";
import imgCritical from "@/assets/demo/criticalpath.jpg";
import imgSwimlane from "@/assets/demo/swimlane.jpg";
import imgBoard from "@/assets/demo/board.jpg";
import imgBaseline from "@/assets/demo/baseline.jpg";
import imgIoFlow from "@/assets/demo/ioflow.jpg";
import imgGlance from "@/assets/demo/glance.jpg";
import imgRisks from "@/assets/demo/risks.jpg";
import imgDecisions from "@/assets/demo/decisions.jpg";
import imgActionItems from "@/assets/demo/actionitems.jpg";
import imgTeamUpdate from "@/assets/demo/teamupdate.jpg";
import imgPfCommand from "@/assets/demo/portfolio.jpg";
import imgPfWeek from "@/assets/demo/pfweek.jpg";
import imgPfTable from "@/assets/demo/pftable.jpg";
import imgPfRaid from "@/assets/demo/pfraid.jpg";
import imgExecSummary from "@/assets/demo/execsummary.jpg";
import imgResIntel from "@/assets/demo/resintel.jpg";
import imgRegistry from "@/assets/demo/registry.jpg";
import imgAskPmo from "@/assets/demo/askpmo.jpg";

import aud_onboarding from "@/assets/demo/audio/onboarding.mp3";
import aud_aiplan from "@/assets/demo/audio/aiplan.mp3";
import aud_wbs from "@/assets/demo/audio/wbs.mp3";
import aud_velocity from "@/assets/demo/audio/velocity.mp3";
import aud_trueup from "@/assets/demo/audio/trueup.mp3";
import aud_autopilot from "@/assets/demo/audio/autopilot.mp3";
import aud_visualmap from "@/assets/demo/audio/visualmap.mp3";
import aud_dashboard from "@/assets/demo/audio/dashboard.mp3";
import aud_raid from "@/assets/demo/audio/raid.mp3";
import aud_statusreport from "@/assets/demo/audio/statusreport.mp3";
import aud_agenda from "@/assets/demo/audio/agenda.mp3";
import aud_notebook from "@/assets/demo/audio/notebook.mp3";
import aud_maestro from "@/assets/demo/audio/maestro.mp3";
import aud_library from "@/assets/demo/audio/library.mp3";
import aud_maestrodoc from "@/assets/demo/audio/maestrodoc.mp3";
import aud_audit from "@/assets/demo/audio/audit.mp3";
import aud_cards from "@/assets/demo/audio/cards.mp3";
import aud_done from "@/assets/demo/audio/done.mp3";
import aud_archived from "@/assets/demo/audio/archived.mp3";
import aud_lessons from "@/assets/demo/audio/lessons.mp3";
import aud_pmo from "@/assets/demo/audio/pmo.mp3";
import aud_wizard from "@/assets/demo/audio/wizard.mp3";
import aud_gantt from "@/assets/demo/audio/gantt.mp3";
import aud_criticalpath from "@/assets/demo/audio/criticalpath.mp3";
import aud_swimlane from "@/assets/demo/audio/swimlane.mp3";
import aud_board from "@/assets/demo/audio/board.mp3";
import aud_baseline from "@/assets/demo/audio/baseline.mp3";
import aud_ioflow from "@/assets/demo/audio/ioflow.mp3";
import aud_glance from "@/assets/demo/audio/glance.mp3";
import aud_risks from "@/assets/demo/audio/risks.mp3";
import aud_decisions from "@/assets/demo/audio/decisions.mp3";
import aud_actionitems from "@/assets/demo/audio/actionitems.mp3";
import aud_teamupdate from "@/assets/demo/audio/teamupdate.mp3";
import aud_featintro from "@/assets/demo/audio/featintro.mp3";
import aud_journeyintro from "@/assets/demo/audio/journeyintro.mp3";
import aud_pmointro from "@/assets/demo/audio/pmointro.mp3";
import aud_pfcommand from "@/assets/demo/audio/pfcommand.mp3";
import aud_pfweek from "@/assets/demo/audio/pfweek.mp3";
import aud_pftable from "@/assets/demo/audio/pftable.mp3";
import aud_pfraid from "@/assets/demo/audio/pfraid.mp3";
import aud_pfglance from "@/assets/demo/audio/pfglance.mp3";
import aud_pfexec from "@/assets/demo/audio/pfexec.mp3";
import aud_pfintel from "@/assets/demo/audio/pfintel.mp3";
import aud_pfregistry from "@/assets/demo/audio/pfregistry.mp3";
import aud_askpmo from "@/assets/demo/audio/askpmo.mp3";
import aud_pfarchive from "@/assets/demo/audio/pfarchive.mp3";
import aud_pfwrap from "@/assets/demo/audio/pfwrap.mp3";

const SCENES = [
  { img: imgOnboarding, pm: "First day in the tool — where do I start?", audio: aud_onboarding, dur: 13, title: "Day one: Maestro says hello", text: "A new PM signs in for the first time. Maestro's guided onboarding asks about their role, methodology and industry — then tailors the whole workspace and offers a quick tour. No manuals, no guesswork." },
  { img: imgAiPlan, img2: imgWbs, fit2: true, pm: "Build me a waterfall plan to deploy our new CRM.", audio: aud_aiplan, dur: 13, title: "\u201CBuild me a waterfall plan to deploy a new CRM\u201D", text: "Prompted to create their first project, the PM simply tells the tool what they need. The AI drafts a complete waterfall plan for the CRM deployment — phases, tasks, durations and dependencies — in seconds." },
  { img: imgWbs, fit: true, pm: "Nice draft — let me tighten the durations and assign my team.", audio: aud_wbs, dur: 14, title: "Tweak the WBS, add the team", text: "The AI's first draft lands in a full WBS. The PM renames tasks, adjusts durations inline, links dependencies, and assigns real people to every task. The plan is now theirs." },
  { img: imgVelocity, pm: "Dev One is thorough, but honestly — he's slow. I'll set that.", audio: aud_velocity, dur: 15, title: "How fast is this team, really?", text: "For each resource the PM picks attributes — base speed (fast / medium / slow), availability, competing priorities, meeting load. Why? Because a plan built for an ideal team slips on a real one. Velocity turns those honest answers into a pace the forecasts can trust." },
  { img: imgTrueup, pm: "Quick check-in: where are we, really?", audio: aud_trueup, dur: 13, title: "The first true-up", text: "A true-up is a quick check-in: the PM walks the open tasks and logs the real % complete. It takes a minute — and it's the heartbeat that keeps the whole plan honest." },
  { img: imgAutopilot, pm: "Auto Pilot — take the wheel between my check-ins.", audio: aud_autopilot, dur: 15, title: "Auto Pilot: the plan updates itself", text: "The PM flips on Auto Pilot. Between true-ups, Auto Pilot progresses tasks at each person's measured velocity, flags forecast slips, and emails check-ins. Every true-up recalibrates it — the more you true-up, the smarter Auto Pilot gets." },
  { img: imgVisualMap, pm: "Same plan — now show it the way my stakeholders think.", audio: aud_visualmap, dur: 14, title: "One WBS, every view", text: "From that single WBS the PM gets a Gantt timeline, Roadmap with critical path, this Visual Map, a Kanban board, Baseline, and the IO Flow — pick the view that fits the audience, no extra work." },
  { img: imgGantt, pm: "Give me the classic Gantt — the steering committee loves it.", audio: aud_gantt, dur: 12, title: "The classic Gantt, one page", text: "One click fits the whole timeline on a single page — bars, dependencies and milestones on real dates. Export it to PDF or PowerPoint for the deck, no redrawing." },
  { img: imgCritical, pm: "Which tasks actually decide my go-live date?", audio: aud_criticalpath, dur: 14, title: "The critical path: 13 days that matter", text: "The roadmap isolates the critical path — here, four tasks and thirteen working days stand between today and go-live. Path health rolls up to one pill, so a slip shows itself before it costs the date." },
  { img: imgSwimlane, pm: "Now lay it out by phase, month by month.", audio: aud_swimlane, dur: 13, title: "Swimlanes on a real calendar", text: "The same plan spreads into swimlanes on a real calendar — phases as lanes, milestones pinned to dates, colors showing health. Zoom from weeks to months, then export the picture for the wall." },
  { img: imgBoard, pm: "The team lives in Kanban — give them a board.", audio: aud_board, dur: 13, title: "A live Kanban board", text: "Every task lands on a live board — To Do, In Progress, Blocked, Done. Drag a card and the plan updates behind it. The team works their way; the plan stays the single source of truth." },
  { img: imgBaseline, pm: "We slipped. Show me exactly where — and what it costs.", audio: aud_baseline, dur: 14, title: "Baseline: the honest comparison", text: "The Baseline view compares today's plan against the one you committed to — 20 tasks slipped, the worst by 27 days, and the cost variance in dollars. Honest answers, ready for the hard status meeting." },
  { img: imgIoFlow, fit: true, pm: "Show each phase as a promise: input in, milestone out.", audio: aud_ioflow, dur: 14, title: "IO Flow: every phase is a contract", text: "The IO Flow turns a phase into a contract — the input it starts from, the work inside, and the milestone it must deliver. Jeopardy is flagged at the top, and one click nudges everyone in the phase." },
  { img: imgDashboard, pm: "Morning coffee, dashboard open. What needs me today?", audio: aud_dashboard, dur: 15, title: "The dashboard: the PM's morning coffee", text: "Overall progress, health, automation activity, workload alerts, actions and risks — one glance shows what needs attention today. The PM starts every day here, then clicks straight into whatever's flashing." },
  { img: imgGlance, pm: "One picture for the exec review, please.", audio: aud_glance, dur: 14, title: "Project at a Glance", text: "Project at a Glance rolls the whole portfolio into a single infographic — task progress, resource load, RAID counts and audit compliance, each with the numbers that matter. Ready for any executive update." },
  { img: imgRaid, pm: "That vendor delay is a risk now — logged, owned, visible.", audio: aud_raid, dur: 14, title: "RAID: risks, actions, issues, decisions", text: "Every project carries risk. In the RAID area the PM logs risks and issues with severity and owners, tracks action items to done, and logs decision points — so nothing lives in someone's inbox." },
  { img: imgRisks, pm: "Plot every risk before it plots against us.", audio: aud_risks, dur: 14, title: "The risk matrix", text: "The risk register scores each risk and plots probability against impact on a live matrix. High and critical exposure jumps out, every entry carries an owner and a mitigation, and meeting notes can feed it automatically." },
  { img: imgDecisions, pm: "Four calls are blocking progress — let's get them decided.", audio: aud_decisions, dur: 13, title: "Decisions: unblock the plan", text: "The Decisions tab tracks every call that needs to be made — open, blocking, overdue — linked to the work it blocks. Pick a decision framework, log the outcome, and unblock the plan." },
  { img: imgActionItems, pm: "Ten action items. Four late. Email the follow-ups — done.", audio: aud_actionitems, dur: 12, title: "Action items that chase themselves", text: "Action items live with owners, due dates and status. The late ones surface first, and one click emails follow-ups to every owner — no more chasing by hand." },
  { img: imgStatus, pm: "Status report due at noon? It's basically done already.", audio: aud_statusreport, dur: 14, title: "Status reports in minutes, not evenings", text: "The Status Report Builder pulls progress, health, milestones and RAID items automatically. The PM picks the sections, adds a sentence of commentary, and shares a polished report — by link, PDF or email." },
  { img: imgAgenda, pm: "Let's walk in with a real agenda — not a blank page.", audio: aud_agenda, dur: 13, title: "Prep the team meeting", text: "The Agenda builder assembles the meeting: open actions, at-risk work, decisions needed. The PM walks in with a focused agenda instead of a blank page." },
  { img: imgNotebook, pm: "Here are my raw meeting notes — pull out the actions for me.", audio: aud_notebook, dur: 15, title: "Raw notes in, RAID items out", text: "After the meeting the PM pastes raw notes into the Notebook. The tool reads them and pulls out the action items, risks and decisions it finds — the PM just ticks the ones to send into RAID. Minutes become follow-through." },
  { img: imgMaestro, pm: "Maestro — summarize this week's accomplishments.", audio: aud_maestro, dur: 15, title: "Meet Project Maestro", text: "Maestro is the PM's AI copilot. Here the PM asks, \u201CGive me a summary of this week's accomplishments\u201D — and gets a crisp, data-backed answer drawn from the actual plan, ready to paste into any update." },
  { img: imgLibrary, pm: "Every charter and BRD — one searchable home.", audio: aud_library, dur: 13, title: "The Library: one home for documents", text: "Charters, BRDs, SOWs, templates and processes live in the Library — versioned, searchable, and attached to the projects they belong to. No more \u201Cwhich folder was that in?\u201D" },
  { img: imgMaestroDoc, pm: "Maestro, read the BRD — what tasks are we missing?", audio: aud_maestrodoc, dur: 15, title: "Maestro mines the BRD", text: "The PM asks Maestro to read the business requirements document stored in the Library and find any tasks the plan is missing. Maestro proposes them; the PM reviews each one and applies the keepers straight into the WBS." },
  { img: imgAudit, pm: "How compliant is this project? Show me what's left.", audio: aud_audit, dur: 14, title: "Audit: how compliant is this project?", text: "The Audit view scores the project against PM best practice — what's done, what's missing, and tips to level up. The PM sees exactly what's left to reach full compliance before anyone else asks." },
  { img: imgCards, pm: "Team — update your own tasks. I'll stop chasing.", audio: aud_cards, dur: 15, title: "Resource cards: ask, don't chase", text: "Time to check on the team. Each person has a card of their open work. The PM sends an update request — the resource gets an email, opens their card, and updates their own tasks. The changes land in the WBS, and the PM's follow-up true-up confirms the plan is current." },
  { img: imgTeamUpdate, fit: true, pm: "No accounts, no chasing — they just click and update.", audio: aud_teamupdate, dur: 15, title: "The no-login update page", text: "Each person gets an email with a no-login link to their own update page. They slide the percent complete, hit submit, and it lands with the PM for confirmation. Updating the plan takes seconds — so it actually happens." },
  { img: imgDone, pm: "One hundred percent. Every task, every milestone.", audio: aud_done, dur: 12, title: "Everything at 100%", text: "Weeks later: every task complete, every milestone hit. The WBS reads 100% and the PM is ready to close the project out properly." },
  { img: imgLessons, pm: "Before we close — what should the next team know?", audio: aud_lessons, dur: 13, title: "Capture the lessons", text: "Before closing, the PM captures lessons learned — what worked, what didn't, what the next team should know. Knowledge stops walking out the door." },
  { img: imgArchived, pm: "Archived — out of the way, never gone.", audio: aud_archived, dur: 13, title: "Archive it — nothing is lost", text: "The PM archives the plan. It moves to the PMO's archived shelf with its full history, reports and lessons intact — out of the way, never gone." },
  { img: imgPmo, pm: "And the PMO can find every lesson we've ever learned.", audio: aud_pmo, dur: 14, title: "The PMO layer sees everything", text: "In the PMO layer, leaders browse archived plans, search lessons learned across every project, and pull insights from the whole portfolio — the organization's project memory in one place." },
  { img: imgWizard, pm: "Next time? I'll give the wizard one date — and go.", audio: aud_wizard, dur: 16, title: "The finale: turn the plan into a wizard", text: "One click turns the finished CRM project into a learned wizard template — it remembers the real durations, the baseline variance and the team's velocity. Next time, the PM enters just one date (a start or a deadline) and gets a complete, honest plan instantly. Experience, reused." },
];

const FEAT_INTRO = {
  intro: {
    eyebrow: "Why PMO Maestro",
    h1: ["Everything a PM needs.", "One place. No juggling."],
    pillars: [
      { Icon: Layers, h: "All in one", p: "Plans, boards, RAID, reports, meetings, documents and an AI copilot — one login, one source of truth." },
      { Icon: Gauge, h: "Auto Pilot + velocity", p: "The plan advances itself between check-ins — at each person's real, measured pace. Honest forecasts, not wishful ones." },
      { Icon: Users, h: "Resource cards", p: "The team updates their own work from a no-login link. The plan stays current without chasing anyone." },
    ],
  },
  audio: aud_featintro,
  dur: 24,
  title: "Everything a PM needs — in one place",
  pm: "One tool that does it all — so I can stop juggling ten.",
  text: "PMO Maestro is the PM's whole toolkit in one place — plans, boards, RAID, reports, meetings, documents and an AI copilot. Auto Pilot keeps the plan moving between check-ins at each person's real, measured velocity. And resource cards let the team update their own work — no logins, no chasing.",
};

const JOURNEY_INTRO = {
  intro: {
    eyebrow: "A day in the life of a PM",
    h1: ["One PM. One project.", "Hello to handoff."],
    pillars: [
      { Icon: Sparkles, h: "AI builds the plan", p: "One prompt becomes a full waterfall plan — phases, tasks, dates and owners in seconds." },
      { Icon: Gauge, h: "Kept honest", p: "True-ups, Auto Pilot and resource velocity keep the forecast tied to reality, not hope." },
      { Icon: Repeat, h: "Finished — and reused", p: "Close at 100%, capture the lessons, and turn the whole project into a one-date wizard." },
    ],
  },
  audio: aud_journeyintro,
  dur: 20,
  title: "One PM. One project. Start to finish.",
  pm: "Follow me — one project, from hello to handoff.",
  text: "This is a day in the life of a PM inside PMO Maestro. Watch one CRM rollout go from a blank workspace to a finished, archived plan — drafted by AI, kept honest by true-ups and Auto Pilot, and reused as a one-date wizard.",
};

const PMO_LEADER = "the PMO leader";
const PMO_SCENES = [
  { img: imgPfCommand, who: PMO_LEADER, pm: "Every project, one screen. What needs me this morning?", audio: aud_pfcommand, dur: 15, title: "The Portfolio command center", text: "The day opens on the Portfolio command center — live KPIs across every project, and an attention list surfacing off-track work, overdue tasks, open risks and late action items. Each one is a click from the project behind it." },
  { img: imgPfWeek, who: PMO_LEADER, pm: "What lands this week — and who's underwater?", audio: aud_pfweek, dur: 14, title: "The week ahead, portfolio-wide", text: "Tasks due across every project, resource pace alerts for anyone running behind their measured velocity, and a workload heat map that shows who's overloaded — before they burn out." },
  { img: imgPfTable, who: PMO_LEADER, pm: "One line per project: health, owner, progress — and a readout.", audio: aud_pftable, dur: 15, title: "Every project on one table", text: "The portfolio table rolls each project into a single row — health, owner, percent complete, flagged tasks and artifacts. One click generates an AI readout for every project that's due. Status reporting for the whole portfolio, in one pass." },
  { img: imgPfRaid, who: PMO_LEADER, pm: "Show me every risk and decision across the portfolio.", audio: aud_pfraid, dur: 13, title: "RAID across the portfolio", text: "Open decisions, the biggest risks and late action items roll up from every project — each linked straight to the plan it came from. Nothing hides in a single project." },
  { img: imgGlance, who: PMO_LEADER, pm: "One picture for the board meeting, please.", audio: aud_pfglance, dur: 13, title: "The portfolio at a glance", text: "Project at a Glance turns the portfolio into a single infographic — task progress, resource load, RAID counts and audit compliance, with the numbers that matter. Ready for any boardroom." },
  { img: imgExecSummary, who: PMO_LEADER, pm: "Write my executive summary — for the whole portfolio.", audio: aud_pfexec, dur: 15, title: "The AI executive summary", text: "One click and the AI drafts a leadership-ready summary across the selected scope — health verdict, highlights, projects needing attention, cross-portfolio risks and outlook — grounded in live data. Edit a sentence, and send." },
  { img: imgResIntel, who: PMO_LEADER, pm: "Who's carrying too much — before it becomes a slipped date?", audio: aud_pfintel, dur: 14, title: "Resource Intelligence", text: "Resource Intelligence reads measured delivery velocity across the organization — who's fast, who's stretched, who's stalled — so staffing calls are made on evidence, not instinct." },
  { img: imgRegistry, who: PMO_LEADER, pm: "Everyone we work with — one registry.", audio: aud_pfregistry, dur: 14, title: "The Resource Registry", text: "The organization-wide record of every person: contacts, roles, influence, reporting lines and measured velocity. New project? The right people — with honest speeds — are one search away." },
  { img: imgAskPmo, who: PMO_LEADER, pm: "Ask the PMO: have we rolled out a CRM before?", audio: aud_askpmo, dur: 14, title: "Ask the PMO", text: "Ask the PMO searches the organization's entire project history with AI — past plans, lessons and outcomes — and answers with the receipts. Institutional memory, on tap." },
  { img: imgArchived, who: PMO_LEADER, pm: "Nothing we've learned ever walks out the door.", audio: aud_pfarchive, dur: 13, title: "Organizational knowledge", text: "Every archived project keeps its full history — plans, reports, RAID and lessons learned — searchable across the whole organization. The next team starts where the last one finished." },
  { img: imgPmo, who: PMO_LEADER, pm: "That's my whole PMO — one login, one source of truth.", audio: aud_pfwrap, dur: 14, title: "The portfolio layer, complete", text: "Command center, board-ready summaries, resource intelligence and organizational memory — sitting on top of every plan your PMs already run in PMO Maestro." },
];

const PMO_INTRO = {
  intro: {
    eyebrow: "A day in the life of a PMO leader",
    h1: ["One leader. Every project.", "The whole portfolio, live."],
    pillars: [
      { Icon: Layers, h: "Portfolio command center", p: "Every project's health, risks and late work on one screen — each item a click from the plan behind it." },
      { Icon: Gauge, h: "Resource intelligence", p: "Measured delivery velocity across the org — spot who's overloaded before it costs a date." },
      { Icon: Sparkles, h: "Organizational memory", p: "AI exec summaries, searchable archives and lessons learned from every project ever run." },
    ],
  },
  audio: aud_pmointro,
  dur: 20,
  who: PMO_LEADER,
  title: "One leader. Every project. One screen.",
  pm: "Twelve projects, one portfolio — and the board meets Thursday. Follow me.",
  text: "This is a day in the life of a PMO leader inside PMO Maestro. One layer above the projects: the whole portfolio on one screen, the organization's project memory on tap, and resource intelligence that spots trouble before it costs a date.",
};

const VIDEOS = {
  journey: {
    scenes: [JOURNEY_INTRO, ...SCENES.slice(0, 10), ...SCENES.slice(28)],
    tag: "A day in the life of a PM",
    h1: "A day in the life of a PM.",
    sub: "End-to-end project planning",
    mins: "~4",
    links: [
      { path: "/demo/features", label: "Feature tour" },
      { path: "/demo/pmo", label: "For PMO leaders" },
      { path: "/demo/highlights", label: "In a hurry? 2-minute highlights" },
    ],
  },
  pmo: {
    scenes: [PMO_INTRO, ...PMO_SCENES],
    tag: "A day in the life of a PMO leader",
    h1: "A day in the life of a PMO leader.",
    sub: "The portfolio layer: command center, exec summaries & org memory",
    mins: "~3",
    links: [
      { path: "/demo", label: "A day in the life of a PM" },
      { path: "/demo/features", label: "Feature tour" },
    ],
  },
  features: {
    scenes: [FEAT_INTRO, ...SCENES.slice(10, 28)],
    tag: "Feature tour",
    h1: "The feature tour.",
    sub: "Boards, baselines, RAID, cards & more",
    mins: "~4",
    links: [
      { path: "/demo", label: "A day in the life of a PM" },
      { path: "/demo/pmo", label: "For PMO leaders" },
      { path: "/demo/highlights", label: "In a hurry? 2-minute highlights" },
    ],
    sections: [
      { at: 0, label: "Why Maestro" },
      { at: 1, label: "Views" },
      { at: 6, label: "Risk" },
      { at: 10, label: "Communicate" },
      { at: 16, label: "Team & compliance" },
    ],
  },
  highlights: {
    scenes: [...[1, 2, 5, 13, 19, 22, 27, 32].map((i) => SCENES[i]), ...[0, 5, 8].map((i) => PMO_SCENES[i])],
    tag: "2-minute highlights",
    h1: "The highlights.",
    sub: "The big moments — for PMs and PMO leaders",
    mins: "~3",
    sections: [
      { at: 0, label: "For PMs" },
      { at: 8, label: "For PMO leaders" },
    ],
    links: [
      { path: "/demo", label: "A day in the life of a PM" },
      { path: "/demo/pmo", label: "For PMO leaders" },
      { path: "/demo/features", label: "Feature tour" },
    ],
    next: { path: "/demo", label: "A day in the life of a PM" },
  },
};

export default function DemoShowcase({ variant = "journey" }) {
  const video = VIDEOS[variant];
  const scenes = video.scenes;
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [idx, setIdx] = useState(() => {
    const c = parseInt(params.get("c"), 10);
    return c >= 1 && c <= scenes.length ? c - 1 : 0;
  });
  const autoStart = useRef(params.get("autoplay") === "1");
  const [playing, setPlaying] = useState(autoStart.current);
  const [splash, setSplash] = useState(!autoStart.current);
  const [ended, setEnded] = useState(false);
  const [nextIn, setNextIn] = useState(null);
  const [voiceover, setVoiceover] = useState(autoStart.current);
  const [music, setMusic] = useState(autoStart.current);
  const [captions, setCaptions] = useState(() => { try { return localStorage.getItem("demo_captions") === "1"; } catch { return false; } });
  const [musicVol, setMusicVol] = useState(() => { try { return Math.min(100, Math.max(0, parseInt(localStorage.getItem("demo_music_vol"), 10) || 50)); } catch { return 50; } });
  const [musicChoice, setMusicChoice] = useState(() => { try { return localStorage.getItem("demo_music_track") || "piano"; } catch { return "piano"; } });
  const [musicMenu, setMusicMenu] = useState(false);
  const [capPhase, setCapPhase] = useState(0);
  const [rated, setRated] = useState(0);
  const [comment, setComment] = useState("");
  const [commentDone, setCommentDone] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [mailMenu, setMailMenu] = useState(false);
  const musicRef = useRef(null);
  const gainRef = useRef(null);
  const audioCtxRef = useRef(null);
  const [chapters, setChapters] = useState(false);
  const timer = useRef(null);
  const audioRef = useRef(null);
  const audioIdx = useRef(-1);
  const [audioDur, setAudioDur] = useState(0);
  const scene = scenes[idx];
  const fillDur = voiceover && scene.audio && audioDur ? audioDur + 0.4 : scene.dur;

  const sessionRef = useRef(`${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);
  const track = useCallback((event, chapter = 0, title = "", rating = 0, comment = "") => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/analytics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ video: variant, event, chapter, title, rating, comment, session: sessionRef.current }),
      keepalive: true,
    }).catch(() => {});
  }, [variant]);

  const lastView = useRef(-1);
  useEffect(() => {
    if (!splash && lastView.current !== idx) {
      lastView.current = idx;
      track("view", idx, scene.title);
    }
  }, [idx, splash, scene.title, track]);

  useEffect(() => { if (ended) track("complete", scenes.length - 1); }, [ended, track, scenes.length]);

  useEffect(() => { if (autoStart.current) track("start", 0); }, [track]);

  useEffect(() => {
    if (ended && video.next) setNextIn(5);
    if (!ended) setNextIn(null);
  }, [ended, video.next]);

  useEffect(() => {
    if (nextIn === null || nextIn < 0) return;
    if (nextIn === 0) { nav(`${video.next.path}?autoplay=1`); return; }
    const t = setTimeout(() => setNextIn((x) => (x === null ? null : x - 1)), 1000);
    return () => clearTimeout(t);
  }, [nextIn, nav, video.next]);

  const go = useCallback((n) => { setEnded(false); setIdx(Math.max(0, Math.min(scenes.length - 1, n))); }, [scenes.length]);

  useEffect(() => { setParams({ c: String(idx + 1) }, { replace: true }); }, [idx, setParams]);

  useEffect(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (voiceover && playing && scene.audio) {
      if (audioIdx.current !== idx) {
        a.src = scene.audio;
        audioIdx.current = idx;
        setAudioDur(0);
      }
      a.onloadedmetadata = () => setAudioDur(a.duration || 0);
      if (a.duration) setAudioDur(a.duration);
      a.onended = () => setIdx((i) => { if (i < scenes.length - 1) return i + 1; setPlaying(false); setEnded(true); return i; });
      a.play().catch(() => {
        setVoiceover(false);
        toast.info("Click the speaker button to enable narration");
      });
    } else {
      a.pause();
    }
    return () => { a.onended = null; a.onloadedmetadata = null; };
  }, [idx, playing, voiceover, scene.audio, scenes.length]);

  useEffect(() => {
    if (!musicRef.current) {
      musicRef.current = new Audio();
      musicRef.current.loop = true;
    }
    const m = musicRef.current;
    const track = MUSIC_TRACKS.find((t) => t.key === musicChoice) || MUSIC_TRACKS[0];
    if (m.dataset.key !== track.key) {
      m.src = track.src;
      m.dataset.key = track.key;
    }
    const target = (voiceover ? 0.11 : 0.4) * (musicVol / 100);
    // iOS ignores HTMLAudio.volume — route through a Web Audio gain node so leveling works on phones too
    if (!gainRef.current && (music || playing)) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const src = ctx.createMediaElementSource(m);
        const gain = ctx.createGain();
        src.connect(gain);
        gain.connect(ctx.destination);
        audioCtxRef.current = ctx;
        gainRef.current = gain;
      } catch {}
    }
    if (gainRef.current) {
      gainRef.current.gain.value = target;
      m.volume = 1;
    } else {
      m.volume = target;
    }
    if (music && playing) {
      audioCtxRef.current?.resume?.().catch?.(() => {});
      m.play().catch(() => setMusic(false));
    } else m.pause();
  }, [music, playing, voiceover, musicVol, musicChoice]);

  useEffect(() => () => { audioRef.current?.pause(); musicRef.current?.pause(); audioCtxRef.current?.close?.().catch?.(() => {}); }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (playing) {
      const secs = voiceover && scene.audio ? scene.dur + 25 : scene.dur;
      timer.current = setTimeout(() => {
        if (idx < scenes.length - 1) setIdx(idx + 1);
        else { setPlaying(false); setEnded(true); }
      }, secs * 1000);
    }
    return () => clearTimeout(timer.current);
  }, [idx, playing, voiceover, scene.dur, scene.audio, scenes.length]);

  useEffect(() => {
    setCapPhase(0);
    if (!captions) return;
    const t = setTimeout(() => setCapPhase(1), Math.max(2500, fillDur * 1000 * 0.28));
    return () => clearTimeout(t);
  }, [idx, captions, fillDur]);

  const prospectEmail = async (kind = "pm") => {
    const o = window.location.origin;
    const text = kind === "exec" ? `Subject: One screen for your whole portfolio — see it in 3 minutes

Hi there,

If you run a PMO, this is worth three minutes. PMO Maestro gives leaders a live portfolio layer on top of every project their PMs run — command center, AI executive summaries, resource intelligence and the organization's full project memory.

Start here:
• A day in the life of a PMO leader (~3 min): ${o}/demo/pmo
• 2-minute highlights: ${o}/demo/highlights
• What your PMs get — a day in the life of a PM (~4 min): ${o}/demo

Every video plays with narration, music and subtitles. The attached QR code jumps straight to the PMO leader tour — handy for decks and printouts.

If it looks like a fit, I'd love to walk your team through a workspace.

Best,
` : `Subject: See PMO Maestro in action — 2 minutes is all it takes

Hi there,

I wanted to share PMO Maestro — one place where a PM plans, tracks, reports and closes projects, with AI doing the heavy lifting.

The fastest way to see it:
• 2-minute highlights: ${o}/demo/highlights
• A day in the life of a PM (~4 min): ${o}/demo
• A day in the life of a PMO leader (~3 min): ${o}/demo/pmo
• The full feature tour (~4 min): ${o}/demo/features

Every video plays with narration, music and subtitles. The attached QR code jumps straight to the 2-minute highlights — handy for decks and printouts.

If it looks like a fit, I'd love to set you up with a workspace.

Best,
`;
    const qrVideo = kind === "exec" ? "pmo" : "highlights";
    const qrLabel = kind === "exec" ? "PMO leader tour" : "highlights";
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${kind === "exec" ? "Exec" : "Prospect"} email copied — paste it into your mail app`);
    } catch { window.prompt("Copy this email:", text); }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo/qr?video=${qrVideo}`, { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `pmo-maestro-${qrVideo}-qr.png`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success(`${qrLabel} QR downloaded — attach it to your email`);
      }
    } catch {}
  };

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?c=${idx + 1}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link copied — opens at chapter ${idx + 1}`);
    } catch { window.prompt("Copy this link:", url); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(idx + 1);
      if (e.key === "ArrowLeft") go(idx - 1);
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, go]);

  return (
    <div className="fixed inset-0 bg-[#060b1a] text-white flex flex-col overflow-hidden" data-testid="demo-showcase">
      <style>{`
        @keyframes kbA { from { transform: scale(1.02) translate(0,0); } to { transform: scale(1.12) translate(-1.6%, -1.2%); } }
        @keyframes kbB { from { transform: scale(1.12) translate(1.4%, 1%); } to { transform: scale(1.02) translate(0,0); } }
        @keyframes demoFill { from { width: 0%; } to { width: 100%; } }
        @keyframes demoRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes demoFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {/* sound splash */}
      {splash && (
        <div className="absolute inset-0 z-30 bg-[#060b1a]/85 backdrop-blur-sm flex items-center justify-center" data-testid="demo-splash">
          <div className="text-center px-6" style={{ animation: "demoRise 500ms ease-out both" }}>
            <p className="text-[11px] font-bold tracking-[0.24em] uppercase text-[#8fb2ff]">PMO Maestro · {video.tag}</p>
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight mt-2">{video.h1}<br />{scenes.length} chapters, {video.mins} minutes.</h1>
            <p className="text-slate-400 mt-3 text-sm">{video.sub}</p>
            <button onClick={() => { track("start", 0); setVoiceover(true); setMusic(true); setPlaying(true); setSplash(false); }}
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#0044FF] hover:bg-[#0038d0] px-8 py-4 text-lg font-bold shadow-[0_0_40px_rgba(0,68,255,0.45)] transition-transform hover:scale-[1.03]" data-testid="demo-splash-play">
              <Play className="h-6 w-6" /> Play with narration &amp; music
            </button>
            <div className="mt-4 flex items-center justify-center gap-5 flex-wrap">
              <button onClick={() => { track("start", 0); setPlaying(true); setSplash(false); }} className="text-sm text-slate-400 hover:text-white underline underline-offset-4" data-testid="demo-splash-silent">
                Watch silently instead
              </button>
              {video.links.map((l, i) => (
                <button key={l.path} onClick={() => nav(l.path)} className="text-sm text-[#8fb2ff] hover:text-white underline underline-offset-4" data-testid={`demo-splash-link-${i}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* end-of-demo CTA */}
      {ended && (
        <div className="absolute inset-0 z-30 bg-[#060b1a]/90 backdrop-blur-sm flex items-center justify-center" data-testid="demo-end-overlay">
          <button onClick={() => setEnded(false)} className="absolute top-5 right-6 text-slate-400 hover:text-white" title="Close" data-testid="demo-end-close"><X className="h-6 w-6" /></button>
          <div className="text-center px-6" style={{ animation: "demoRise 500ms ease-out both" }}>
            <p className="text-[11px] font-bold tracking-[0.24em] uppercase text-[#8fb2ff]">That's the whole journey</p>
            <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight mt-2">{variant === "features" ? <>That's what's under the hood.<br />Your turn.</> : variant === "pmo" ? <>Your whole portfolio,<br />one source of truth. Your turn.</> : <>From day one to a<br />learned wizard. Your turn.</>}</h1>
            <p className="text-slate-400 mt-4 text-sm sm:text-base">Everything you just watched takes minutes to set up — starting with one plan.</p>
            <div className="mt-5" data-testid="demo-rating">
              {rated ? (
                commentDone ? (
                  <p className="text-sm text-[#8fb2ff]" data-testid="demo-rating-thanks">Thanks for the feedback!</p>
                ) : (
                  <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="demo-rating-comment-box">
                    <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={200} autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter" && comment.trim()) { track("comment", 0, "", rated, comment); setCommentDone(true); } }}
                      placeholder="One line — what stood out, or what was missing?"
                      className="w-72 sm:w-96 rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#7ea6ff]"
                      data-testid="demo-rating-comment-input" />
                    <button onClick={() => { if (comment.trim()) track("comment", 0, "", rated, comment); setCommentDone(true); }}
                      className="rounded-full bg-[#0044FF] hover:bg-[#0038d0] px-4 py-2 text-sm font-semibold" data-testid="demo-rating-comment-send">Send</button>
                    <button onClick={() => setCommentDone(true)} className="text-sm text-slate-400 hover:text-white underline underline-offset-4" data-testid="demo-rating-comment-skip">Skip</button>
                  </div>
                )
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-slate-400 mr-1">How was the demo?</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => { setRated(n); track("rating", 0, "", n); }}
                      className="text-slate-500 hover:text-[#ffc53d] hover:scale-110 transition-transform" title={`${n} star${n > 1 ? "s" : ""}`} data-testid={`demo-rating-star-${n}`}>
                      <Star className="h-6 w-6" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            {video.next && nextIn !== null && nextIn >= 0 && (
              <p className="mt-4 text-sm text-[#8fb2ff]" data-testid="demo-end-next">
                Up next: <span className="font-semibold">{video.next.label}</span> in {nextIn}s —{" "}
                <button onClick={() => setNextIn(-1)} className="underline underline-offset-4 text-slate-400 hover:text-white" data-testid="demo-end-next-cancel">stay here</button>
              </p>
            )}
            <button onClick={() => { track("cta", idx); nav(user ? "/build" : "/register"); }}
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#0044FF] hover:bg-[#0038d0] px-8 py-4 text-lg font-bold shadow-[0_0_40px_rgba(0,68,255,0.45)] transition-transform hover:scale-[1.03]" data-testid="demo-end-cta">
              Start your first plan <ArrowRight className="h-5 w-5" />
            </button>
            <div className="mt-5 flex items-center justify-center gap-6 flex-wrap">
              <button onClick={() => { go(0); setPlaying(true); }} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white underline underline-offset-4" data-testid="demo-end-replay">
                <RotateCcw className="h-3.5 w-3.5" /> Replay
              </button>
              {video.links.map((l, i) => (
                <button key={l.path} onClick={() => nav(l.path)} className="text-sm text-[#8fb2ff] hover:text-white underline underline-offset-4" data-testid={`demo-end-link-${i}`}>
                  {l.label}
                </button>
              ))}
              {!user && (
                <button onClick={() => nav("/login")} className="text-sm text-slate-400 hover:text-white underline underline-offset-4" data-testid="demo-end-signin">
                  I have an account
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR dialog */}
      {qrOpen && (
        <div className="absolute inset-0 z-40 bg-[#060b1a]/85 backdrop-blur-sm flex items-center justify-center" onClick={() => setQrOpen(false)} data-testid="demo-qr-overlay">
          <div className="bg-[#0b1226] border border-white/10 rounded-2xl p-8 text-center max-w-sm" onClick={(e) => e.stopPropagation()} style={{ animation: "demoRise 400ms ease-out both" }}>
            <p className="text-[11px] font-bold tracking-[0.24em] uppercase text-[#8fb2ff]">QR code · {video.tag}</p>
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/demo/qr?video=${variant}`} alt={`QR code for the ${video.tag} demo`} className="mx-auto mt-4 w-56 h-56 rounded-lg bg-white p-2" data-testid="demo-qr-image" />
            <p className="text-xs text-slate-400 mt-4">Points to your live demo — drop it into slide decks, one-pagers and printouts.</p>
            <div className="mt-4 flex items-center justify-center gap-4">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/demo/qr?video=${variant}`} download={`pmo-maestro-${variant}-qr.png`} className="text-sm font-semibold text-[#8fb2ff] hover:text-white underline underline-offset-4" data-testid="demo-qr-download">Download PNG</a>
              <button onClick={() => setQrOpen(false)} className="text-sm text-slate-400 hover:text-white underline underline-offset-4" data-testid="demo-qr-close">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* top bar */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2.5 sm:py-3 shrink-0">
        <p className="font-display font-extrabold tracking-tight text-sm sm:text-base whitespace-nowrap">PMO Maestro<span className="text-[#7ea6ff] hidden md:inline"> · {video.tag}</span></p>
        <div className="flex-1 min-w-[50px] h-1 rounded-full bg-white/10 overflow-hidden flex gap-px">
          {scenes.map((s, i) => (
            <button key={i} onClick={() => { go(i); setPlaying(true); }} className="relative flex-1 h-full bg-white/10" title={s.title} data-testid={`demo-progress-${i}`}>
              <span className="absolute inset-y-0 left-0 bg-[#7ea6ff]" style={{
                width: i < idx ? "100%" : "0%",
                ...(i === idx ? { animation: `demoFill ${fillDur}s linear forwards`, animationPlayState: playing ? "running" : "paused" } : {}),
              }} />
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400 tabular-nums shrink-0">{idx + 1}/{scenes.length}</span>
        <button onClick={() => { setVoiceover((v) => { if (!v) setMusic(true); return !v; }); }} className={`${voiceover ? "text-[#8fb2ff]" : "text-slate-300"} hover:text-white`} title={voiceover ? "Mute narration" : "Play spoken narration"} data-testid="demo-voiceover-toggle">
          {voiceover ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </button>
        <div className="relative">
          <button onClick={() => setMusicMenu((v) => !v)} className={`${music ? "text-[#8fb2ff]" : "text-slate-300"} hover:text-white`} title="Music — pick a track or turn it off" data-testid="demo-music-toggle">
            {music ? <Music className="h-5 w-5" /> : <Music2 className="h-5 w-5 opacity-60" />}
          </button>
          {musicMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMusicMenu(false)} />
              <div className="absolute right-0 top-8 z-40 w-52 rounded-xl border border-white/15 bg-[#0b1226]/95 backdrop-blur-md p-2 shadow-[0_20px_60px_rgba(0,0,0,0.5)]" data-testid="demo-music-menu">
                <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[#8fb2ff]">Music</p>
                {MUSIC_TRACKS.map((t) => (
                  <button key={t.key}
                    onClick={() => { setMusicChoice(t.key); setMusic(true); try { localStorage.setItem("demo_music_track", t.key); } catch {} }}
                    className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-white hover:bg-white/10" data-testid={`demo-music-track-${t.key}`}>
                    <span>{t.label}</span>
                    {music && musicChoice === t.key && <Check className="h-4 w-4 text-[#8fb2ff]" />}
                  </button>
                ))}
                <button onClick={() => setMusic(false)} className="w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-white/10" data-testid="demo-music-off">
                  <span>Off</span>
                  {!music && <Check className="h-4 w-4 text-[#8fb2ff]" />}
                </button>
                <div className="px-3 pt-2 pb-1.5">
                  <input type="range" min="0" max="100" value={musicVol}
                    onChange={(e) => { const v = parseInt(e.target.value, 10); setMusicVol(v); try { localStorage.setItem("demo_music_vol", String(v)); } catch {} }}
                    className="w-full accent-[#7ea6ff] cursor-pointer" title={`Music volume: ${musicVol}%`} aria-label="Music volume" data-testid="demo-music-volume" />
                </div>
              </div>
            </>
          )}
        </div>
        <button onClick={() => setCaptions((c) => { const n = !c; try { localStorage.setItem("demo_captions", n ? "1" : "0"); } catch {} return n; })} className={`${captions ? "text-[#8fb2ff]" : "text-slate-300"} hover:text-white`} title={captions ? "Hide subtitles" : "Show subtitles"} data-testid="demo-captions-toggle">
          <Subtitles className="h-5 w-5" />
        </button>
        <button onClick={copyLink} className="hidden sm:block text-slate-300 hover:text-white" title="Copy a link that opens at this chapter" data-testid="demo-share-button"><LinkIcon className="h-5 w-5" /></button>
        {user && (
          <div className="relative hidden sm:block">
            <button onClick={() => setMailMenu((m) => !m)} className={`${mailMenu ? "text-[#8fb2ff]" : "text-slate-300"} hover:text-white`} title="Copy a ready-to-send outreach email with demo links" data-testid="demo-prospect-email">
              <Mail className="h-5 w-5" />
            </button>
            {mailMenu && (
              <div className="absolute right-0 top-8 z-40 w-64 rounded-xl border border-white/15 bg-[#0b1226]/95 backdrop-blur-md p-2 shadow-[0_20px_60px_rgba(0,0,0,0.5)]" data-testid="demo-mail-menu">
                <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[#8fb2ff]">Outreach email</p>
                <button onClick={() => { setMailMenu(false); prospectEmail("pm"); }} className="w-full text-left rounded-lg px-3 py-2 hover:bg-white/10" data-testid="demo-mail-pm">
                  <span className="block text-sm font-semibold">For PM prospects</span>
                  <span className="block text-xs text-slate-400 mt-0.5">Opens with the 2-min highlights</span>
                </button>
                <button onClick={() => { setMailMenu(false); prospectEmail("exec"); }} className="w-full text-left rounded-lg px-3 py-2 hover:bg-white/10" data-testid="demo-mail-exec">
                  <span className="block text-sm font-semibold">For PMO / exec buyers</span>
                  <span className="block text-xs text-slate-400 mt-0.5">Opens with the portfolio tour</span>
                </button>
              </div>
            )}
          </div>
        )}
        {user && (
          <button onClick={() => setQrOpen(true)} className="hidden sm:block text-slate-300 hover:text-white" title="QR code for this video — for slide decks and printouts" data-testid="demo-qr-button">
            <QrCode className="h-5 w-5" />
          </button>
        )}
        <button onClick={() => setChapters((c) => !c)} className="text-slate-300 hover:text-white lg:hidden" title="Chapters" data-testid="demo-chapters-toggle"><List className="h-5 w-5" /></button>
        <button onClick={() => nav(-1)} className="text-slate-300 hover:text-white" title="Exit demo" data-testid="demo-exit-button"><X className="h-5 w-5" /></button>
      </div>

      <div className="flex-1 flex min-h-0 px-3 sm:px-6 pb-3 sm:pb-4 gap-3 sm:gap-4">
        {/* stage */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 min-h-0 rounded-xl border border-white/10 bg-[#0b1226] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
            <div className="h-8 flex items-center gap-1.5 px-3 border-b border-white/10 bg-white/5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" /><span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" /><span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
              <span className="ml-3 text-[11px] text-slate-400 truncate">app.pmomaestro.com</span>
            </div>
            <div className="relative h-[calc(100%-2rem)] overflow-hidden">
              {scene.intro ? (
                <div key={idx} className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#0a1230] via-[#0b1226] to-[#12204a] px-8 overflow-y-auto" data-testid="demo-intro-slide">
                  <p className="text-[11px] font-bold tracking-[0.24em] uppercase text-[#8fb2ff]" style={{ animation: "demoRise 500ms ease-out both" }}>{scene.intro.eyebrow}</p>
                  <h2 className="font-display text-2xl sm:text-4xl xl:text-5xl font-extrabold tracking-tight text-center mt-3" style={{ animation: "demoRise 600ms ease-out 150ms both" }}>{scene.intro.h1[0]}<br />{scene.intro.h1[1]}</h2>
                  <div className="grid sm:grid-cols-3 gap-4 mt-8 w-full max-w-4xl">
                    {scene.intro.pillars.map((c, k) => (
                      <div key={c.h} className="rounded-xl border border-white/10 bg-white/5 backdrop-blur px-5 py-6 text-center" style={{ animation: `demoRise 600ms ease-out ${350 + k * 220}ms both` }} data-testid={`demo-intro-pillar-${k}`}>
                        <c.Icon className="h-7 w-7 mx-auto text-[#7ea6ff]" />
                        <p className="font-display font-extrabold mt-3">{c.h}</p>
                        <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{c.p}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
              <>
              <img key={idx} src={scene.img} alt={scene.title} className={`absolute inset-0 w-full h-full ${scene.fit ? "object-contain" : "object-cover object-center"}`}
                style={{ animation: `${idx % 2 ? "kbB" : "kbA"} ${fillDur + 2}s ease-in-out forwards`, animationPlayState: playing ? "running" : "paused" }} data-testid="demo-scene-image" />
              {scene.img2 && (
                <img key={`b-${idx}`} src={scene.img2} alt="" className={`absolute inset-0 w-full h-full ${scene.fit2 ? "object-contain bg-[#0b1226]" : "object-cover object-center"}`}
                  style={{ opacity: 0, animation: `demoFadeIn 900ms ease-in-out ${Math.max(2, fillDur * 0.45)}s forwards, ${idx % 2 ? "kbA" : "kbB"} ${fillDur + 2}s ease-in-out forwards`, animationPlayState: playing ? "running" : "paused" }} data-testid="demo-scene-image-2" />
              )}
              </>
              )}
              {captions && !scene.intro && (
                <div key={`cc-${idx}`} className="absolute bottom-3 inset-x-0 z-10 flex justify-center px-6 pointer-events-none" data-testid="demo-captions">
                  <p className="max-w-[92%] sm:max-w-[80%] text-center bg-black/70 text-white text-xs sm:text-base px-3 py-1.5 sm:px-3.5 rounded-md leading-snug" style={{ animation: "demoFadeIn 300ms ease-out both" }}>
                    {capPhase === 0 && scene.pm ? <em>“{scene.pm}” — {scene.who || "the PM"}</em> : scene.text}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* caption + controls */}
          <div key={`cap-${idx}`} className="mt-2 sm:mt-3 flex items-end gap-2 sm:gap-3 flex-wrap" style={{ animation: "demoRise 500ms ease-out both" }}>
            <div className="flex-1 min-w-[220px] rounded-xl border border-white/10 bg-white/5 backdrop-blur px-3.5 py-2.5 sm:px-4 sm:py-3">
              <p className="text-[10px] sm:text-[11px] font-bold tracking-[0.18em] uppercase text-[#8fb2ff]">Step {idx + 1} of {scenes.length}</p>
              <h2 className="font-display text-base sm:text-xl font-extrabold mt-0.5" data-testid="demo-scene-title">{scene.title}</h2>
              {scene.pm && <p className="text-xs sm:text-sm italic text-[#8fb2ff] mt-1" data-testid="demo-scene-pm-quote">“{scene.pm}” <span className="not-italic text-slate-500 text-xs">— {scene.who || "the PM"}</span></p>}
              <p className="text-xs sm:text-sm text-slate-300 mt-1 leading-relaxed line-clamp-3 sm:line-clamp-none" data-testid="demo-scene-text">{scene.text}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => go(idx - 1)} disabled={idx === 0} className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 disabled:opacity-30" data-testid="demo-prev-button"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => setPlaying((p) => !p)} className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-[#0044FF] hover:bg-[#0038d0] flex items-center justify-center" data-testid="demo-play-button">
                {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <button onClick={() => go(idx + 1)} disabled={idx === scenes.length - 1} className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 disabled:opacity-30" data-testid="demo-next-button"><ChevronRight className="h-5 w-5" /></button>
              <button onClick={() => { go(0); setPlaying(true); }} className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10" title="Restart" data-testid="demo-restart-button"><RotateCcw className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {/* chapters rail */}
        <aside className={`${chapters ? "block absolute inset-y-12 right-2 z-20 w-[85vw] max-w-xs bg-[#0b1226] border border-white/10 rounded-xl p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-y-auto" : "hidden"} lg:static lg:block lg:w-64 xl:w-72 shrink-0 overflow-y-auto rounded-xl lg:border lg:border-white/10 lg:bg-white/[0.03] lg:p-2 lg:shadow-none`} data-testid="demo-chapter-list">
          {scenes.map((s, i) => {
            const sec = video.sections?.find((x) => x.at === i);
            return (
            <div key={i}>
              {sec && <p className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[#8fb2ff]" data-testid={`demo-section-${i}`}>{sec.label}</p>}
            <button onClick={() => { go(i); setPlaying(true); setChapters(false); }}
              className={`w-full text-left rounded-lg px-2 py-1.5 text-xs transition-colors flex items-center gap-2.5 ${i === idx ? "bg-[#0044FF]/25 text-white border border-[#7ea6ff]/40" : "text-slate-400 hover:bg-white/5 border border-transparent"}`}
              data-testid={`demo-chapter-${i}`}>
              <span className="relative shrink-0 w-16 h-10 rounded-md overflow-hidden border border-white/10 bg-white/5">
                {s.img ? (
                  <img src={s.img} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" data-testid={`demo-chapter-thumb-${i}`} />
                ) : (
                  <span className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0a1230] to-[#12204a]"><Layers className="h-4 w-4 text-[#7ea6ff]" /></span>
                )}
                <span className="absolute bottom-0.5 left-1 text-[9px] font-bold tabular-nums text-white bg-black/60 rounded px-1 leading-3">{String(i + 1).padStart(2, "0")}</span>
              </span>
              <span className="min-w-0 leading-snug">{s.title}</span>
            </button>
            </div>
          ); })}
        </aside>
      </div>
    </div>
  );
}
